import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Card,
  CardContent,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Divider
} from '@mui/material';
import {
  Add,
  Send,
  Edit,
  Delete,
  Visibility,
  Schedule,
  Campaign,
  Email,
  NotificationsActive,
  Info,
  Warning,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface PushNotification {
  id: string;
  title: string;
  body: string;
  targetAudience: string;
  status: 'scheduled' | 'sent' | 'failed';
  scheduledFor?: string;
  sentAt?: string;
  delivered: number;
  opened: number;
  deepLink?: string;
  imageUrl?: string;
}

interface EmailCampaign {
  id: string;
  subject: string;
  templateName: string;
  targetAudience: string;
  status: 'draft' | 'scheduled' | 'sent';
  scheduledFor?: string;
  sentAt?: string;
  sent: number;
  opened: number;
  clicked: number;
}

interface SystemAnnouncement {
  id: string;
  title: string;
  message: string;
  priority: 'info' | 'warning' | 'critical';
  startDate: string;
  endDate: string;
  dismissible: boolean;
  active: boolean;
}

const NotificationsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [pushNotifications, setPushNotifications] = useState<PushNotification[]>([]);
  const [emailCampaigns, setEmailCampaigns] = useState<EmailCampaign[]>([]);
  const [announcements, setAnnouncements] = useState<SystemAnnouncement[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Push Notification Dialog
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushTarget, setPushTarget] = useState('all');
  const [pushDeepLink, setPushDeepLink] = useState('');
  const [pushScheduled, setPushScheduled] = useState(false);
  const [pushScheduleDate, setPushScheduleDate] = useState('');

  // Email Campaign Dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailTarget, setEmailTarget] = useState('all');
  const [emailScheduled, setEmailScheduled] = useState(false);
  const [emailScheduleDate, setEmailScheduleDate] = useState('');

  // Announcement Dialog
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [announcementPriority, setAnnouncementPriority] = useState<'info' | 'warning' | 'critical'>('info');
  const [announcementStartDate, setAnnouncementStartDate] = useState('');
  const [announcementEndDate, setAnnouncementEndDate] = useState('');
  const [announcementDismissible, setAnnouncementDismissible] = useState(true);

  const [alert, setAlert] = useState<{ show: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    show: false,
    message: '',
    severity: 'success'
  });

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    fetchData();
  }, [tabValue]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      if (tabValue === 0) {
        const response = await axios.get(`${apiUrl}/admin/notifications/push`, { headers });
        setPushNotifications(response.data.notifications || []);
      } else if (tabValue === 1) {
        const response = await axios.get(`${apiUrl}/admin/notifications/email`, { headers });
        setEmailCampaigns(response.data.campaigns || []);
      } else if (tabValue === 2) {
        const response = await axios.get(`${apiUrl}/admin/notifications/announcements`, { headers });
        setAnnouncements(response.data.announcements || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch data:', error);
      showAlert('error', error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPushNotification = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const notificationData = {
        title: pushTitle,
        body: pushBody,
        targetAudience: pushTarget,
        deepLink: pushDeepLink,
        scheduledFor: pushScheduled ? pushScheduleDate : null
      };

      await axios.post(
        `${apiUrl}/admin/notifications/push`,
        notificationData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', pushScheduled ? 'Push notification scheduled successfully' : 'Push notification sent successfully');
      setPushDialogOpen(false);
      resetPushForm();
      fetchData();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to send push notification');
    }
  };

  const handleSendEmailCampaign = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const campaignData = {
        subject: emailSubject,
        templateName: emailTemplate,
        body: emailBody,
        targetAudience: emailTarget,
        scheduledFor: emailScheduled ? emailScheduleDate : null
      };

      await axios.post(
        `${apiUrl}/admin/notifications/email`,
        campaignData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', emailScheduled ? 'Email campaign scheduled successfully' : 'Email campaign sent successfully');
      setEmailDialogOpen(false);
      resetEmailForm();
      fetchData();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to send email campaign');
    }
  };

  const handleCreateAnnouncement = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const announcementData = {
        title: announcementTitle,
        message: announcementMessage,
        priority: announcementPriority,
        startDate: announcementStartDate,
        endDate: announcementEndDate,
        dismissible: announcementDismissible
      };

      await axios.post(
        `${apiUrl}/admin/notifications/announcements`,
        announcementData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Announcement created successfully');
      setAnnouncementDialogOpen(false);
      resetAnnouncementForm();
      fetchData();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${apiUrl}/admin/notifications/announcements/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Announcement deleted successfully');
      fetchData();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to delete announcement');
    }
  };

  const resetPushForm = () => {
    setPushTitle('');
    setPushBody('');
    setPushTarget('all');
    setPushDeepLink('');
    setPushScheduled(false);
    setPushScheduleDate('');
  };

  const resetEmailForm = () => {
    setEmailSubject('');
    setEmailTemplate('');
    setEmailBody('');
    setEmailTarget('all');
    setEmailScheduled(false);
    setEmailScheduleDate('');
  };

  const resetAnnouncementForm = () => {
    setAnnouncementTitle('');
    setAnnouncementMessage('');
    setAnnouncementPriority('info');
    setAnnouncementStartDate('');
    setAnnouncementEndDate('');
    setAnnouncementDismissible(true);
  };

  const showAlert = (severity: 'success' | 'error' | 'info', message: string) => {
    setAlert({ show: true, message, severity });
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'sent': return 'success';
      case 'scheduled': return 'primary';
      case 'failed': return 'error';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'info': return <Info color="info" />;
      case 'warning': return <Warning color="warning" />;
      case 'critical': return <ErrorIcon color="error" />;
      default: return <Info />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Notifications & Communication
        </Typography>
        {tabValue === 0 && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setPushDialogOpen(true)}
          >
            New Push Notification
          </Button>
        )}
        {tabValue === 1 && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setEmailDialogOpen(true)}
          >
            New Email Campaign
          </Button>
        )}
        {tabValue === 2 && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAnnouncementDialogOpen(true)}
          >
            New Announcement
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<NotificationsActive />} label="Push Notifications" iconPosition="start" />
          <Tab icon={<Email />} label="Email Campaigns" iconPosition="start" />
          <Tab icon={<Campaign />} label="System Announcements" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Push Notifications Tab */}
      <TabPanel value={tabValue} index={0}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Target Audience</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Delivered</TableCell>
                <TableCell>Opened</TableCell>
                <TableCell>Open Rate</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pushNotifications.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((notification) => (
                <TableRow key={notification.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{notification.title}</Typography>
                    <Typography variant="caption" color="textSecondary">{notification.body}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={notification.targetAudience} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={notification.status} color={getStatusColor(notification.status)} size="small" />
                  </TableCell>
                  <TableCell>{notification.delivered.toLocaleString()}</TableCell>
                  <TableCell>{notification.opened.toLocaleString()}</TableCell>
                  <TableCell>
                    {notification.delivered > 0
                      ? ((notification.opened / notification.delivered) * 100).toFixed(1) + '%'
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {notification.sentAt
                      ? new Date(notification.sentAt).toLocaleDateString()
                      : notification.scheduledFor
                      ? new Date(notification.scheduledFor).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small">
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pushNotifications.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          />
        </TableContainer>
      </TabPanel>

      {/* Email Campaigns Tab */}
      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Subject</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Target Audience</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Opened</TableCell>
                <TableCell>Clicked</TableCell>
                <TableCell>Open Rate</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emailCampaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.subject}</TableCell>
                  <TableCell>
                    <Chip label={campaign.templateName} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={campaign.targetAudience} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={campaign.status} color={getStatusColor(campaign.status)} size="small" />
                  </TableCell>
                  <TableCell>{campaign.sent.toLocaleString()}</TableCell>
                  <TableCell>{campaign.opened.toLocaleString()}</TableCell>
                  <TableCell>{campaign.clicked.toLocaleString()}</TableCell>
                  <TableCell>
                    {campaign.sent > 0
                      ? ((campaign.opened / campaign.sent) * 100).toFixed(1) + '%'
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {campaign.sentAt
                      ? new Date(campaign.sentAt).toLocaleDateString()
                      : campaign.scheduledFor
                      ? new Date(campaign.scheduledFor).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small">
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={emailCampaigns.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          />
        </TableContainer>
      </TabPanel>

      {/* System Announcements Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 2 }}>
          {announcements.map((announcement) => (
            <Card key={announcement.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {getPriorityIcon(announcement.priority)}
                    <Typography variant="h6">{announcement.title}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={announcement.priority}
                      color={
                        announcement.priority === 'critical' ? 'error' :
                        announcement.priority === 'warning' ? 'warning' : 'info'
                      }
                      size="small"
                    />
                    <Chip
                      label={announcement.active ? 'Active' : 'Inactive'}
                      color={announcement.active ? 'success' : 'default'}
                      size="small"
                    />
                    {announcement.dismissible && (
                      <Chip label="Dismissible" size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {announcement.message}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(announcement.startDate).toLocaleDateString()} - {new Date(announcement.endDate).toLocaleDateString()}
                  </Typography>
                  <Box>
                    <IconButton size="small" color="primary">
                      <Edit />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteAnnouncement(announcement.id)}>
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {announcements.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Campaign sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography color="textSecondary">
              No announcements yet. Click "New Announcement" to create one.
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Push Notification Dialog */}
      <Dialog open={pushDialogOpen} onClose={() => setPushDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Push Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Notification Title"
              fullWidth
              value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              required
            />
            <TextField
              label="Notification Body"
              fullWidth
              multiline
              rows={3}
              value={pushBody}
              onChange={(e) => setPushBody(e.target.value)}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Target Audience</InputLabel>
              <Select
                value={pushTarget}
                label="Target Audience"
                onChange={(e) => setPushTarget(e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="drivers">Drivers Only</MenuItem>
                <MenuItem value="passengers">Passengers Only</MenuItem>
                <MenuItem value="active">Active Users (Last 30 days)</MenuItem>
                <MenuItem value="inactive">Inactive Users (30+ days)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Deep Link (Optional)"
              fullWidth
              value={pushDeepLink}
              onChange={(e) => setPushDeepLink(e.target.value)}
              helperText="E.g., /rides, /profile, /payments"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={pushScheduled}
                  onChange={(e) => setPushScheduled(e.target.checked)}
                />
              }
              label="Schedule for later"
            />
            {pushScheduled && (
              <TextField
                label="Schedule Date & Time"
                type="datetime-local"
                fullWidth
                value={pushScheduleDate}
                onChange={(e) => setPushScheduleDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPushDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={pushScheduled ? <Schedule /> : <Send />}
            onClick={handleSendPushNotification}
          >
            {pushScheduled ? 'Schedule' : 'Send Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Campaign Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Email Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Email Subject"
              fullWidth
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Email Template</InputLabel>
              <Select
                value={emailTemplate}
                label="Email Template"
                onChange={(e) => setEmailTemplate(e.target.value)}
              >
                <MenuItem value="welcome">Welcome Email</MenuItem>
                <MenuItem value="promotional">Promotional</MenuItem>
                <MenuItem value="newsletter">Newsletter</MenuItem>
                <MenuItem value="announcement">Announcement</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Email Body"
              fullWidth
              multiline
              rows={8}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              helperText="Use {{firstName}}, {{lastName}}, {{email}} for personalization"
              required
            />
            <FormControl fullWidth>
              <InputLabel>Target Audience</InputLabel>
              <Select
                value={emailTarget}
                label="Target Audience"
                onChange={(e) => setEmailTarget(e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="drivers">Drivers Only</MenuItem>
                <MenuItem value="passengers">Passengers Only</MenuItem>
                <MenuItem value="active">Active Users (Last 30 days)</MenuItem>
                <MenuItem value="inactive">Inactive Users (30+ days)</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={emailScheduled}
                  onChange={(e) => setEmailScheduled(e.target.checked)}
                />
              }
              label="Schedule for later"
            />
            {emailScheduled && (
              <TextField
                label="Schedule Date & Time"
                type="datetime-local"
                fullWidth
                value={emailScheduleDate}
                onChange={(e) => setEmailScheduleDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={emailScheduled ? <Schedule /> : <Send />}
            onClick={handleSendEmailCampaign}
          >
            {emailScheduled ? 'Schedule' : 'Send Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* System Announcement Dialog */}
      <Dialog open={announcementDialogOpen} onClose={() => setAnnouncementDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create System Announcement</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Announcement Title"
              fullWidth
              value={announcementTitle}
              onChange={(e) => setAnnouncementTitle(e.target.value)}
              required
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              value={announcementMessage}
              onChange={(e) => setAnnouncementMessage(e.target.value)}
              required
            />
            <FormControl>
              <FormLabel>Priority Level</FormLabel>
              <RadioGroup
                value={announcementPriority}
                onChange={(e) => setAnnouncementPriority(e.target.value as any)}
              >
                <FormControlLabel value="info" control={<Radio />} label="Info (Blue)" />
                <FormControlLabel value="warning" control={<Radio />} label="Warning (Orange)" />
                <FormControlLabel value="critical" control={<Radio />} label="Critical (Red)" />
              </RadioGroup>
            </FormControl>
            <TextField
              label="Start Date"
              type="datetime-local"
              fullWidth
              value={announcementStartDate}
              onChange={(e) => setAnnouncementStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="End Date"
              type="datetime-local"
              fullWidth
              value={announcementEndDate}
              onChange={(e) => setAnnouncementEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={announcementDismissible}
                  onChange={(e) => setAnnouncementDismissible(e.target.checked)}
                />
              }
              label="Allow users to dismiss"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnnouncementDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateAnnouncement}>
            Create Announcement
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <Alert onClose={() => setAlert({ ...alert, show: false })} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsPage;
